import { useRef, useState, useEffect } from "react";
import { Box, Chip, CircularProgress, Divider } from "@mui/material";
import { Layers2 } from "lucide-react";
import PopperMenu, { createMenuOption } from "./PopperMenu";
import useIsMobile from "../hooks/useIsMobile";
import {
  useCreateDocument,
  useListFeaturedDocuments,
} from "services/documents";
import { DocumentType } from "protogen/documents_pb";
import { ListFeaturedDocumentsRequest } from "protogen/documents_service_pb";
import { useNavigate } from "react-router-dom";

interface TemplatesButtonProps {
  onTemplateSelect: (documentId: string) => void;
  isLoadingTemplate?: boolean;
  getTemplateContent?: () => {
    textContent: string;
    htmlContent: string;
    title: string;
  };
}

const TemplatesButton = ({
  onTemplateSelect,
  isLoadingTemplate: externalIsLoading = false,
  getTemplateContent,
}: TemplatesButtonProps) => {
  const templatesButtonRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [templatesPopperOpen, setTemplatesPopperOpen] = useState(false);
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  const [templateMenuOptions, setTemplateMenuOptions] = useState<
    ReturnType<typeof createMenuOption>[]
  >([]);
  const { request: createDocumentRequest } = useCreateDocument();
  const navigate = useNavigate();
  const { request: listDocumentsRequest } = useListFeaturedDocuments();

  const isLoadingTemplate = externalIsLoading || internalIsLoading;

  const createTemplate = async () => {
    if (!getTemplateContent) return;
    const content = getTemplateContent?.();
    const doc = await createDocumentRequest({
      familyRef: "",
      title: `Email Template: ${content.title}`,
      textContent: content.textContent,
      content: content.htmlContent,
      attachments: [],
      documentType: DocumentType.NOTE,
      topics: ["Templates / Email"],
    });
    if (doc) {
      navigate(`/documents/${encodeURIComponent(doc.document?.uuid || "")}`);
    }
  };

  // Load templates only when menu is opened
  useEffect(() => {
    if (!templatesPopperOpen || templateMenuOptions.length > 0) return;

    const loadTemplates = async () => {
      const response = await listDocumentsRequest(
        new ListFeaturedDocumentsRequest({
          anyTopics: ["Email template", "Templates / Email"],
        }),
      );

      if (!response) return;

      const options = response.featuredDocuments.map((featuredDoc) => {
        const title = featuredDoc.document?.title || "Untitled";
        const displayTitle = title.replace(/^Email Template:\s*/, "");
        return createMenuOption(displayTitle, () => {
          handleTemplateSelect(featuredDoc.document?.uuid || "");
        });
      });

      setTemplateMenuOptions([
        ...options,
        <Divider key="divider" sx={{ margin: "2px 0" }} />,
        createMenuOption(
          "Create template",
          () => {
            createTemplate();
          },
          undefined,
          true,
        ),
      ]);
    };

    loadTemplates();
  }, [templatesPopperOpen]);

  // Reset internal loading when external loading changes
  useEffect(() => {
    if (!externalIsLoading) {
      setInternalIsLoading(false);
    }
  }, [externalIsLoading]);

  const handleTemplateSelect = (documentId: string) => {
    setTemplatesPopperOpen(false);
    setInternalIsLoading(true);
    onTemplateSelect(documentId);
  };

  return (
    <Box ref={templatesButtonRef}>
      <Chip
        label="Templates"
        onClick={() => {
          if (!isLoadingTemplate) {
            setTemplatesPopperOpen(true);
          }
        }}
        icon={
          isLoadingTemplate ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <Layers2 size={16} strokeWidth={1.5} />
          )
        }
        variant="outlined"
        size={isMobile ? "medium" : "small"}
        sx={{
          height: isMobile ? "40px" : undefined,
          borderRadius: "100px",
          opacity: isLoadingTemplate ? 0.7 : 1,
          cursor: isLoadingTemplate ? "not-allowed" : "pointer",
        }}
      />
      <PopperMenu
        open={templatesPopperOpen}
        anchorRef={templatesButtonRef}
        handleClose={() => {
          setTemplatesPopperOpen(false);
        }}
        placement="top"
        title={isMobile ? "Templates" : undefined}
      >
        {templateMenuOptions}
      </PopperMenu>
    </Box>
  );
};

export default TemplatesButton;
